<script lang="ts" context="module">
  export type SubmitEvent = {
    name: string;
    icon?: string;
    iconDark?: string;
    cancelLoading: () => void;
  };
</script>

<script lang="ts">
  import { createEventDispatcher } from "svelte";
  import Button from "$components/+design-system/Button/Button.svelte";
  import { alerts } from "$stores/alert";
  import Modal from "$components/+shared/Modal/Modal.svelte";
  import OVIcon from "app/components/+shared/OVIcon/OVIcon.svelte";
  import ImageField from "app/components/form/Fields/ImageField.svelte";
  import { Moon, Sun } from "app/components/+shared/OVIcon";
  import Form from "app/components/form/Form/Form.svelte";
  import InputField from "app/components/form/Fields/InputField.svelte";

  export let open: boolean;
  export let title: string;
  export let confirmLabel = "Confirm";
  export let name = "";
  export let icon: string | null | undefined = undefined;
  export let iconDark: string | null | undefined = undefined;
  export let loading = false;
  export let blankErrorMessage = "";

  /**
   * Cancel loading state of confirm/submit button
   */
  const cancelLoading = () => {
    loading = false;
  };

  $: if (!open) {
    cancelLoading();
  }

  const dispatch = createEventDispatcher<{
    submit: SubmitEvent;
  }>();

  const onSubmit = async ({ detail }: CustomEvent) => {
    if (detail?.iconDark && !detail?.icon) {
      alerts.error("Please select a light mode icon");
      return;
    }
    // display error if input blank
    if (blankErrorMessage) {
      if (name.length < 1) {
        alerts.error(blankErrorMessage);
        loading = false;
        return;
      }
      loading = true;
      dispatch("submit", {
        name,
        icon: detail.icon,
        iconDark: detail.iconDark,
        cancelLoading,
      });
      // clear input value after submit
      name = "";
    }
  };
</script>

<Modal bind:open maxWidth="max-w-[400px]">
  <div class="flex flex-col gap-6 p-6">
    <h3 class="text-md font-medium text-gray-600 dark:text-gray-200">
      {title}
    </h3>

    <Form
      name="category"
      class="flex w-full flex-col items-start gap-4"
      on:submit={onSubmit}
      defaultValues={{
        name,
        icon,
        iconDark,
      }}
    >
      <InputField label="Name" name="name" bind:value={name} autoFocus wrapperClassname="w-full" />

      <div class="text-sm font-medium text-gray-500 dark:text-gray-300">
        Custom icons (light / dark)
      </div>
      <div class="mb-5 flex gap-6">
        <div class="flex items-center gap-1">
          <OVIcon icon={Sun} class="text-gray-400" />
          <ImageField
            name="icon"
            label="Select light mode icon"
            labelHidden
            omitDefaultBackground
            size="size-[24px]"
            uploadedSize="size-[16px]"
            radius="rounded-[3px]"
            backgroundColor="bg-transparent dark:bg-neutral-500"
            wrapperClassname="mb-0"
          />
        </div>
        <div class="flex items-center gap-1">
          <OVIcon icon={Moon} class="text-gray-400" />

          <ImageField
            name="iconDark"
            label="Select dark mode icon"
            labelHidden
            omitDefaultBackground
            size="size-[24px]"
            uploadedSize="size-[16px]"
            radius="rounded-[3px]"
            backgroundColor="bg-neutral-800 dark:bg-transparent"
            wrapperClassname="mb-0"
          />
        </div>
      </div>

      <div class="dark:transparent flex w-full gap-3 pt-2">
        <Button
          on:click={() => (open = false)}
          variant="secondary"
          label="Cancel"
          size="large"
          isFullWidth
        />
        <Button
          htmlButtonType="submit"
          variant="primary"
          size="large"
          label={confirmLabel}
          disabled={loading || name.length < 1}
          isFullWidth
        />
      </div>
    </Form>
  </div>
</Modal>
